import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../constants/api';

const APP_STORE_URL = 'https://apps.apple.com/app/spotto/id6740039498';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=pl.spotto.mobile';
const APP_SCHEME = 'spottospace';
const APP_ID = '6740039498';

type BusinessProfile = {
  id: string;
  displayName: string;
  companyName: string;
  address: string;
  city: string;
  postalCode?: string;
  description?: string;
  avatarUrl?: string;
  phone?: string;
  email?: string;
  facilityTypes?: string[];
};

// Helper to update or create meta tag
const setMetaTag = (property: string, content: string, isName = false) => {
  const attr = isName ? 'name' : 'property';
  let element = document.querySelector(`meta[${attr}="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, property);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

export const BookBusiness: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const { t } = useTranslation();
  const [business, setBusiness] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  const getAvatarUrl = (url?: string) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${BASE_URL}${url}`;
  };

  // Update meta tags when business data is loaded
  useEffect(() => {
    if (business) {
      const ogImageUrl = business.avatarUrl
        ? getAvatarUrl(business.avatarUrl)
        : 'https://spotto.pl/icon.png';

      document.title = `${t('bookBusiness.bookAt')} ${business.displayName} - Spotto`;

      // Open Graph meta tags
      setMetaTag('og:title', `${t('bookBusiness.bookAt')} ${business.displayName}`);
      setMetaTag('og:description', t('bookBusiness.ogDescription'));
      setMetaTag('og:image', ogImageUrl || '');
      setMetaTag('og:url', `https://spotto.pl/book/${businessId}`);
      setMetaTag('og:type', 'website');

      // Twitter Card meta tags
      setMetaTag('twitter:card', 'summary_large_image', true);
      setMetaTag('twitter:title', `${t('bookBusiness.bookAt')} ${business.displayName}`, true);
      setMetaTag('twitter:description', t('bookBusiness.ogDescription'), true);
      setMetaTag('twitter:image', ogImageUrl || '', true);

      // iOS Smart App Banner
      setMetaTag('apple-itunes-app', `app-id=${APP_ID}, app-argument=${APP_SCHEME}://book/${businessId}`, true);
    }

    return () => {
      // Cleanup: Reset title on unmount
      document.title = 'Spotto - Sport Facility Scheduler';
    };
  }, [business, businessId, t]);

  useEffect(() => {
    fetchBusiness();

    // Try to open app on mobile
    if (isMobile && !attempted) {
      tryOpenApp();
    }
  }, [businessId]);

  const fetchBusiness = async () => {
    if (!businessId) return;

    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/business-profile/${businessId}/public`);
      if (!res.ok) {
        throw new Error('Business not found');
      }
      const data = await res.json();
      setBusiness(data);
    } catch (err) {
      console.error('Failed to fetch business:', err);
      setError(t('bookBusiness.businessNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const tryOpenApp = () => {
    setAttempted(true);
    const appUrl = `${APP_SCHEME}://book/${businessId}`;

    // Try to open the app
    window.location.href = appUrl;

    // Fallback to store after timeout (if app didn't open)
    setTimeout(() => {
      window.location.href = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
    }, 2500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface-dark to-surface-dark-elevated flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="mb-8">
        <img src="/icon.png" alt="Spotto" className="w-20 h-20 rounded-full logo-glow" />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-ink-on-dark-muted text-center">
          <div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('bookBusiness.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <p className="text-brand-warm mb-4">{error}</p>
        </div>
      )}

      {/* Business Preview */}
      {business && !loading && (
        <div className="bg-surface-dark-elevated rounded-2xl p-6 max-w-md w-full shadow-xl border border-white/10 mb-8">
          <div className="flex items-center gap-4 mb-4">
            {business.avatarUrl ? (
              <img
                src={getAvatarUrl(business.avatarUrl) || ''}
                alt={business.displayName}
                className="w-16 h-16 rounded-xl object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-brand-primary rounded-xl flex items-center justify-center text-2xl text-ink-on-dark font-bold">
                {business.displayName?.charAt(0).toUpperCase() || 'S'}
              </div>
            )}
            <div>
              <h1 className="font-display text-xl font-semibold tracking-tight text-ink-on-dark">{business.displayName}</h1>
              {business.companyName && business.companyName !== business.displayName && (
                <p className="text-ink-on-dark-muted text-xs">{business.companyName}</p>
              )}
            </div>
          </div>

          {business.description && (
            <p className="text-ink-on-dark text-sm mb-4">{business.description}</p>
          )}

          {/* Contact Info */}
          <div className="space-y-2 pt-4 border-t border-white/10">
            {/* Address */}
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-ink-on-dark-muted mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div className="text-ink-on-dark text-sm">
                <p>{business.address}</p>
                <p>{business.postalCode ? `${business.postalCode} ` : ''}{business.city}</p>
              </div>
            </div>

            {/* Phone */}
            {business.phone && (
              <a href={`tel:${business.phone}`} className="flex items-center gap-3 text-ink-on-dark hover:text-brand-primary transition-colors">
                <svg className="w-5 h-5 text-ink-on-dark-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-sm">{business.phone}</span>
              </a>
            )}

            {/* Email */}
            {business.email && (
              <a href={`mailto:${business.email}`} className="flex items-center gap-3 text-ink-on-dark hover:text-brand-primary transition-colors">
                <svg className="w-5 h-5 text-ink-on-dark-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm">{business.email}</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Download Section */}
      <div className="text-center max-w-md">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-ink-on-dark mb-2">{t('bookBusiness.downloadTitle')}</h2>
        <p className="text-ink-on-dark-muted mb-6">{t('bookBusiness.downloadDescription')}</p>

        {/* Store Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-black hover:bg-surface-dark-elevated text-ink-on-dark px-6 py-3 rounded-xl transition-colors border border-white/10"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <div className="text-left">
              <div className="text-xs text-ink-on-dark-muted">{t('bookBusiness.downloadOn')}</div>
              <div className="text-lg font-semibold">App Store</div>
            </div>
          </a>

          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-black hover:bg-surface-dark-elevated text-ink-on-dark px-6 py-3 rounded-xl transition-colors border border-white/10"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            <div className="text-left">
              <div className="text-xs text-ink-on-dark-muted">{t('bookBusiness.getItOn')}</div>
              <div className="text-lg font-semibold">Google Play</div>
            </div>
          </a>
        </div>

        {/* Open in App Button (Mobile only) */}
        {isMobile && (
          <button
            onClick={tryOpenApp}
            className="w-full bg-brand-primary hover:bg-brand-primary-hover text-ink-on-dark font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            {t('bookBusiness.openInApp')}
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 text-center text-ink-on-dark-muted text-sm">
        <p>© {new Date().getFullYear()} Spotto. {t('bookBusiness.allRightsReserved')}</p>
      </div>
    </div>
  );
};
